// src/pages/admin/AdminPerformanceDashboard.js
import API from '../../services/api';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FiTrendingUp, FiDollarSign, FiUsers, FiShoppingBag,
  FiMapPin, FiSmartphone, FiMonitor, FiStar, FiActivity,
  FiTarget, FiPercent, FiAlertCircle
} from 'react-icons/fi';
import { toast } from 'react-toastify';

// Chart.js
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const AdminPerformanceDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('30');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchAnalytics = useCallback(async () => {
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }
    
    try {
     
      const res = await API.get('/admin/analytics', { params: { days: dateRange } });
      
    
      
      if (res?.data?.success && res.data.data) {
        if (mountedRef.current) setAnalytics(res.data.data);
       
      } else {
        throw new Error('Réponse invalide du serveur');
      }
    } catch (error) {
     
      const errorMsg = error.response?.data?.message || error.message || 'Erreur lors du chargement des analytics';
      toast.error(errorMsg);
      if (mountedRef.current) {
        setError(errorMsg);
        setAnalytics(null);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement des analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-md">
          <FiAlertCircle className="text-red-500 text-6xl mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchAnalytics}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FiAlertCircle className="text-gray-400 text-6xl mx-auto mb-4" />
          <p className="text-gray-500">Aucune donnée disponible</p>
          <button
            onClick={fetchAnalytics}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Recharger
          </button>
        </div>
      </div>
    );
  }

  const {
    revenueGrowth = 0,
    userGrowth = 0,
    orderGrowth = 0,
    conversionGrowth = 0,
    thisMonthRevenue = 0,
    thisMonthOrders = 0,
    thisMonthUsers = 0,
    ordersByStatus = {},
    aov = 0,
    conversionRate = 0,
    customerSegmentation = {},
    devices = {},
    topRegions = [],
    avgRating = 0,
    retentionRate = 0,
    recentActivity = [],
    salesTrend = []
  } = analytics;

  const totalOrders =
    (ordersByStatus.completed || 0) +
    (ordersByStatus.pending || 0) +
    (ordersByStatus.cancelled || 0);

  const getPercent = (val, total) =>
    total > 0 ? Math.round((val / total) * 100) : 0;

  // Badge de croissance : « — » quand pas de base de comparaison (serveur renvoie null),
  // sinon valeur signée. Évite l'affichage cassé « +% ».
  const fmtBadge = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`);

  const iconMap = {
    FiShoppingBag: FiShoppingBag,
    FiUsers: FiUsers,
    FiTarget: FiTarget,
    FiStar: FiStar
  };

  const colorMap = {
    green: 'text-green-500',
    blue: 'text-blue-500',
    purple: 'text-purple-500',
    yellow: 'text-yellow-500',
    orange: 'text-orange-500'
  };

  // Chart data pour la tendance des ventes
  const salesChartData = {
    labels: salesTrend.map((item) => item.date),
    datasets: [
      {
        label: 'Revenu (MAD)',
        data: salesTrend.map((item) => item.revenue),
        borderColor: 'rgba(59,130,246,1)',
        backgroundColor: 'rgba(59,130,246,0.1)',
        borderWidth: 2,
        tension: 0.3,
        yAxisID: 'y'
      },
      {
        label: 'Commandes',
        data: salesTrend.map((item) => item.orders),
        borderColor: 'rgba(16,185,129,1)',
        backgroundColor: 'rgba(16,185,129,0.1)',
        borderWidth: 2,
        tension: 0.3,
        yAxisID: 'y1'
      }
    ]
  };

  const salesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      legend: {
        display: true
      },
      tooltip: {
        enabled: true
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        beginAtZero: true,
        position: 'left',
        title: {
          display: true,
          text: 'Revenu (MAD)'
        }
      },
      y1: {
        beginAtZero: true,
        position: 'right',
        grid: {
          drawOnChartArea: false
        },
        title: {
          display: true,
          text: 'Commandes'
        }
      }
    }
  };

  return (
    <div className="p-6 pb-24">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tableau de Performance</h1>
            <p className="text-gray-600 mt-1">Analytics et insights en temps réel</p>
          </div>
          <div className="flex gap-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">7 derniers jours</option>
              <option value="30">30 derniers jours</option>
              <option value="90">90 derniers jours</option>
              <option value="365">12 derniers mois</option>
            </select>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
              disabled={loading}
            >
              <FiActivity className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      {/* Cartes du haut */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <FiDollarSign className="text-3xl opacity-80" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm">
              {fmtBadge(revenueGrowth)}
            </span>
          </div>
          <p className="text-sm opacity-90">Croissance Revenue</p>
          <p className="text-3xl font-bold mt-1">{thisMonthRevenue.toLocaleString()} MAD</p>
          <p className="text-sm opacity-75 mt-1">Ce mois</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <FiUsers className="text-3xl opacity-80" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm">
              {fmtBadge(userGrowth)}
            </span>
          </div>
          <p className="text-sm opacity-90">Nouveaux Utilisateurs</p>
          <p className="text-3xl font-bold mt-1">{thisMonthUsers}</p>
          <p className="text-sm opacity-75 mt-1">Ce mois</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <FiShoppingBag className="text-3xl opacity-80" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm">
              {fmtBadge(orderGrowth)}
            </span>
          </div>
          <p className="text-sm opacity-90">Commandes</p>
          <p className="text-3xl font-bold mt-1">{thisMonthOrders}</p>
          <p className="text-sm opacity-75 mt-1">Ce mois</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <FiTarget className="text-3xl opacity-80" />
            <span className="bg-white bg-opacity-20 px-2 py-1 rounded text-sm">
              {fmtBadge(conversionGrowth)}
            </span>
          </div>
          <p className="text-sm opacity-90">Taux de Conversion</p>
          <p className="text-3xl font-bold mt-1">{conversionRate}%</p>
          <p className="text-sm opacity-75 mt-1">Moyenne</p>
        </div>
      </div>

      {/* Tendance + Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FiTrendingUp className="mr-2 text-primary-600" />
            Tendance des Ventes
          </h3>
          {salesTrend.length === 0 ? (
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
              <div className="text-center">
                <FiAlertCircle className="text-gray-400 text-4xl mx-auto mb-2" />
                <p className="text-gray-500">Aucune donnée de vente pour cette période</p>
                <p className="text-gray-400 text-sm mt-1">Les ventes apparaîtront ici une fois les commandes complétées</p>
              </div>
            </div>
          ) : (
            <div className="h-64">
              <Line data={salesChartData} options={salesChartOptions} />
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Distribution des Commandes</h3>
          {totalOrders === 0 ? (
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded">
              <div className="text-center">
                <FiShoppingBag className="text-gray-400 text-4xl mx-auto mb-2" />
                <p className="text-gray-500">Aucune commande pour cette période</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Complétées</span>
                  <span className="text-sm font-semibold">{ordersByStatus.completed || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${getPercent(ordersByStatus.completed, totalOrders)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">En Attente</span>
                  <span className="text-sm font-semibold">{ordersByStatus.pending || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-yellow-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${getPercent(ordersByStatus.pending, totalOrders)}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-gray-600">Annulées</span>
                  <span className="text-sm font-semibold">{ordersByStatus.cancelled || 0}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${getPercent(ordersByStatus.cancelled, totalOrders)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Segmentation / Devices / Régions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FiUsers className="mr-2 text-blue-600" />
            Segmentation Clients
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Nouveaux clients</span>
              <span className="font-semibold text-blue-600">{customerSegmentation.new || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Clients réguliers</span>
              <span className="font-semibold text-green-600">{customerSegmentation.returning || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Clients VIP</span>
              <span className="font-semibold text-purple-600">{customerSegmentation.vip || 0}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FiSmartphone className="mr-2 text-purple-600" />
            Devices Utilisés
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <FiSmartphone className="mr-2 text-gray-400" />
                <span className="text-sm text-gray-600">Mobile</span>
              </div>
              <span className="font-semibold">{devices.mobile || 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <FiMonitor className="mr-2 text-gray-400" />
                <span className="text-sm text-gray-600">Desktop</span>
              </div>
              <span className="font-semibold">{devices.desktop || 0}%</span>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <FiActivity className="mr-2 text-gray-400" />
                <span className="text-sm text-gray-600">Tablet</span>
              </div>
              <span className="font-semibold">{devices.tablet || 0}%</span>
            </div>
            {((devices.mobile || 0) + (devices.desktop || 0) + (devices.tablet || 0)) === 0 && (
              <p className="text-xs text-gray-400 pt-1">
                Données en cours de collecte sur cette période.
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FiMapPin className="mr-2 text-orange-600" />
            Top Régions
          </h3>
          <div className="space-y-3">
            {topRegions.length === 0 ? (
              <div className="text-center py-8">
                <FiMapPin className="text-gray-400 text-3xl mx-auto mb-2" />
                <p className="text-gray-500 text-sm">Aucune donnée régionale disponible</p>
              </div>
            ) : (
              topRegions.map((region, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">{region.name}</span>
                  <span className="font-semibold">{region.percentage}%</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* AOV / CR / Rating / Retention */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <FiDollarSign className="text-green-500 text-2xl" />
            <span className="text-xs text-gray-500">AOV</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{aov} MAD</p>
          <p className="text-sm text-gray-600">Panier moyen</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <FiPercent className="text-blue-500 text-2xl" />
            <span className="text-xs text-gray-500">CR</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{conversionRate}%</p>
          <p className="text-sm text-gray-600">Taux de conversion</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <FiStar className="text-yellow-500 text-2xl" />
            <span className="text-xs text-gray-500">Rating</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{avgRating}</p>
          <p className="text-sm text-gray-600">Satisfaction client</p>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-2">
            <FiActivity className="text-purple-500 text-2xl" />
            <span className="text-xs text-gray-500">Retention</span>
          </div>
          <p className="text-2xl font-bold text-gray-900">{retentionRate}%</p>
          <p className="text-sm text-gray-600">Taux de rétention</p>
        </div>
      </div>

      {/* Activité en temps réel */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FiActivity className="mr-2 text-primary-600" />
          Activité en Temps Réel
        </h3>
        <div className="space-y-3">
          {recentActivity.length > 0 ? (
            recentActivity.map((activity, index) => {
              const IconComponent = iconMap[activity.icon] || FiActivity;
              const colorClass = colorMap[activity.color] || 'text-gray-500';
              return (
                <div key={index} className="flex items-center justify-between py-2 border-b last:border-0">
                  <div className="flex items-center">
                    <IconComponent className={`${colorClass} mr-3`} />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.user}</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-400">{activity.time}</span>
                </div>
              );
            })
          ) : (
            <div className="text-center py-8">
              <FiActivity className="text-gray-400 text-3xl mx-auto mb-2" />
              <p className="text-gray-500">Aucune activité récente</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPerformanceDashboard;